import { useEffect, useMemo, useRef, useState } from 'react'
import FixedEquipmentEditor, { compileFixedEquipment } from './FixedEquipmentEditor'
import { GameAssetIcon } from './GameAssetIcon'
import WeaponAffixEditor, { compileModifierRolls } from './WeaponAffixEditor'
import { readPlannerDraft, writePlannerDraft } from './planner-draft'
import type {
  Affix,
  Build,
  CalculationResponse,
  FixedEquipmentSlot,
  FixedItemSelection,
  FixedItemSelections,
  Skill,
  SkillRow,
  Support,
  SupportStat,
  Weapon,
} from './planner-types'

const API_URL = (import.meta.env.VITE_API_URL || 'http://localhost:8000').replace(/\/$/, '')
const WORKSPACE_ROWS = 5
const SUPPORTS_PER_SKILL = 5
const emptyRows = (): SkillRow[] => Array.from({ length: WORKSPACE_ROWS }, () => ({ skillId: '', supportIds: [] }))

const slotClass: Record<string, string> = {
  Helm1: 'helmet',
  Amulet1: 'amulet',
  BodyArmour1: 'body',
  Ring1: 'ring-left',
  Ring2: 'ring-right',
  Gloves1: 'gloves',
  Boots1: 'boots',
  Belt1: 'belt',
}
const slotLabel: Record<string, string> = {
  Helm1: 'Helmet', Amulet1: 'Amulet', BodyArmour1: 'Body Armour', Ring1: 'Ring', Ring2: 'Ring',
  Gloves1: 'Gloves', Boots1: 'Boots', Belt1: 'Belt',
}

const fmt = (value: unknown, decimals = 1) => typeof value === 'number' && Number.isFinite(value)
  ? new Intl.NumberFormat('en-US', { maximumFractionDigits: decimals }).format(value)
  : '—'

function numberFrom(value: unknown, keys: string[]): number | undefined {
  if (!value || typeof value !== 'object') return undefined
  const record = value as Record<string, unknown>
  for (const key of keys) {
    const hit = record[key]
    if (typeof hit === 'number' && Number.isFinite(hit)) return hit
  }
  for (const child of Object.values(record)) {
    const nested = numberFrom(child, keys)
    if (nested !== undefined) return nested
  }
  return undefined
}

function supportStatValue(stat: SupportStat) {
  if (stat.value_num !== null) return fmt(stat.value_num, 3)
  if (stat.value_bool !== null) return stat.value_bool ? 'true' : 'false'
  return stat.value_text || '—'
}
function supportEffectText(support: Support) {
  return support.stats?.length
    ? support.stats.map(stat => `${stat.stat_id} = ${supportStatValue(stat)}`).join(' · ')
    : `${support.stat_count} source operation${support.stat_count === 1 ? '' : 's'}`
}
function friendlyPool(slotName: string | undefined) {
  if (!slotName) return 'source pool unresolved'
  if (slotName === 'pool_1') return 'Source pool 1'
  if (slotName === 'pool_2') return 'Source pool 2'
  if (slotName === 'pool_3') return 'Source pool 3'
  return slotName
}
async function json<T>(url: string): Promise<T> {
  const response = await fetch(url)
  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    const detail = payload && typeof payload === 'object' && 'detail' in payload ? (payload as { detail?: unknown }).detail : null
    throw new Error(typeof detail === 'string' ? detail : `Catalog request failed (${response.status})`)
  }
  return payload as T
}
function boundedNumeric(value: string, label: string, min: number, max: number, integer = false) {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed < min || parsed > max || (integer && !Number.isInteger(parsed))) {
    throw new Error(`${label} must be ${integer ? 'a whole number ' : ''}from ${min} to ${max}.`)
  }
  return parsed
}
function positiveNumeric(value: string, label: string) {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) throw new Error(`${label} must be greater than 0.`)
  return parsed
}

function EquipmentSlot({ className, label, enabled, value, active, onClick }: {
  className: string; label: string; enabled?: boolean; value?: string | null; active?: boolean; onClick?: () => void
}) {
  return <button
    className={`pp-equip-slot ${className} ${enabled ? 'is-enabled' : 'is-locked'} ${active ? 'is-active' : ''}`}
    type="button"
    disabled={!enabled}
    onClick={onClick}
    title={enabled ? label : `${label}: not calculation-active yet`}
  >
    <span>{label}</span><strong>{value || (enabled ? 'Select' : 'Locked')}</strong>
  </button>
}

export default function ProductionPlanner() {
  const initialDraft = useRef(readPlannerDraft()).current
  const hydrating = useRef(Boolean(initialDraft))
  const [draftReady, setDraftReady] = useState(!initialDraft)
  const [apiHealth, setApiHealth] = useState<'checking' | 'ready' | 'down'>('checking')

  const [builds, setBuilds] = useState<Build[]>([])
  const [buildId, setBuildId] = useState(initialDraft?.buildId || '')
  const [skills, setSkills] = useState<Skill[]>([])
  const [rows, setRows] = useState<SkillRow[]>(initialDraft?.rows || emptyRows)
  const [focus, setFocus] = useState(initialDraft?.focus ?? 0)
  const [supports, setSupports] = useState<Support[]>([])
  const [supportCache, setSupportCache] = useState<Record<string, Support[]>>({})
  const [supportSearch, setSupportSearch] = useState('')

  const [weapons, setWeapons] = useState<Weapon[]>([])
  const [weaponId, setWeaponId] = useState(initialDraft?.weaponId || '')
  const [itemLevel, setItemLevel] = useState(initialDraft?.itemLevel || '100')
  const [affixes, setAffixes] = useState<Affix[]>([])
  const [selectedAffixes, setSelectedAffixes] = useState<string[]>(initialDraft?.selectedAffixes || [])
  const [rolls, setRolls] = useState<Record<string, Record<string, string>>>(initialDraft?.rolls || {})

  const [fixedSlots, setFixedSlots] = useState<FixedEquipmentSlot[]>([])
  const [activeFixedSlot, setActiveFixedSlot] = useState('')
  const [fixedSelections, setFixedSelections] = useState<FixedItemSelections>(initialDraft?.fixedEquipment || {})

  const [level, setLevel] = useState(initialDraft?.level || '83')
  const [enemyFire, setEnemyFire] = useState(initialDraft?.enemyFire || '0')
  const [enemyCold, setEnemyCold] = useState(initialDraft?.enemyCold || '0')
  const [enemyLightning, setEnemyLightning] = useState(initialDraft?.enemyLightning || '0')
  const [enemyChaos, setEnemyChaos] = useState(initialDraft?.enemyChaos || '0')
  const [targetHits, setTargetHits] = useState(initialDraft?.targetHits || '')
  const [dotUptime, setDotUptime] = useState(initialDraft?.dotUptime || '')
  const [result, setResult] = useState<CalculationResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const build = useMemo(() => builds.find(value => value.build_id === buildId), [builds, buildId])
  const visibleSkills = useMemo(() => skills.filter(value => !value.hidden_from_default_picker), [skills])
  const focusedSkillId = rows[focus]?.skillId || ''
  const focusedSkill = useMemo(() => skills.find(value => value.skill_id === focusedSkillId), [skills, focusedSkillId])
  const focusedSupports = rows[focus]?.supportIds || []
  const selectedSkillIds = useMemo(() => new Set(rows.map(row => row.skillId).filter(Boolean)), [rows])
  const weapon = useMemo(() => weapons.find(value => value.base_item_id === weaponId), [weapons, weaponId])
  const selectedDefs = useMemo(() => selectedAffixes.map(id => affixes.find(value => value.modifier_id === id)).filter((value): value is Affix => Boolean(value)), [affixes, selectedAffixes])
  const selectedSupportRows = useMemo(() => focusedSupports.map(id => supports.find(value => value.support_id === id)).filter((value): value is Support => Boolean(value)), [focusedSupports, supports])
  const filteredSupports = useMemo(() => {
    const needle = supportSearch.trim().toLowerCase()
    if (!needle) return supports
    return supports.filter(support => `${support.name || ''} ${support.support_id} ${support.support_family_id || ''} ${supportEffectText(support)}`.toLowerCase().includes(needle))
  }, [supports, supportSearch])
  const activeSlot = fixedSlots.find(slot => slot.slot_id === activeFixedSlot)

  useEffect(() => {
    let cancelled = false
    const check = () => fetch(`${API_URL}/health`, { cache: 'no-store' })
      .then(response => { if (!response.ok) throw new Error(`health ${response.status}`); if (!cancelled) setApiHealth('ready') })
      .catch(() => { if (!cancelled) setApiHealth('down') })
    check()
    const timer = window.setInterval(check, 60_000)
    return () => { cancelled = true; window.clearInterval(timer) }
  }, [])

  useEffect(() => {
    Promise.all([
      json<Build[]>(`${API_URL}/api/catalog/builds`),
      json<FixedEquipmentSlot[]>(`${API_URL}/api/catalog/equipment/slots`),
    ]).then(([buildRows, slotRows]) => {
      setBuilds(buildRows)
      setFixedSlots(slotRows)
      setActiveFixedSlot(current => current || slotRows.find(slot => slot.slot_id === 'Amulet1')?.slot_id || slotRows[0]?.slot_id || '')
      const restored = initialDraft?.buildId && buildRows.some(value => value.build_id === initialDraft.buildId) ? initialDraft.buildId : ''
      const first = buildRows.find(value => !value.infamous) || buildRows[0]
      setBuildId(restored || first?.build_id || '')
      if (!restored && hydrating.current) { hydrating.current = false; setDraftReady(true) }
    }).catch(value => { setApiHealth('down'); setError(value instanceof Error ? value.message : String(value)) })
  }, [])

  useEffect(() => {
    if (!buildId) return
    const restoring = hydrating.current && initialDraft?.buildId === buildId
    setSkills([]); setSupports([]); setSupportCache({}); setWeapons([]); setResult(null); setError(''); setSupportSearch('')
    if (!restoring) {
      setRows(emptyRows()); setFocus(0); setWeaponId(''); setAffixes([]); setSelectedAffixes([]); setRolls({})
    }
    json<Skill[]>(`${API_URL}/api/catalog/builds/${encodeURIComponent(buildId)}/skills`).then(data => {
      setSkills(data)
      const candidates = data.filter(value => !value.hidden_from_default_picker)
      const allowed = new Set(candidates.map(value => value.skill_id))
      if (restoring && initialDraft) {
        const restoredRows = initialDraft.rows.slice(0, WORKSPACE_ROWS).map(row => allowed.has(row.skillId)
          ? { skillId: row.skillId, supportIds: row.supportIds.slice(0, SUPPORTS_PER_SKILL) }
          : { skillId: '', supportIds: [] })
        while (restoredRows.length < WORKSPACE_ROWS) restoredRows.push({ skillId: '', supportIds: [] })
        setRows(restoredRows)
        const restoredFocus = Math.min(WORKSPACE_ROWS - 1, Math.max(0, initialDraft.focus))
        setFocus(restoredRows[restoredFocus]?.skillId ? restoredFocus : 0)
      } else {
        const preferred = candidates.find(value => value.slot_name === 'pool_2') || candidates.find(value => value.slot_name === 'pool_1') || candidates[0]
        const next = emptyRows()
        if (preferred) next[0] = { skillId: preferred.skill_id, supportIds: [] }
        setRows(next); setFocus(0)
      }
      if (!restoring || !initialDraft?.rows.some(row => allowed.has(row.skillId))) { hydrating.current = false; setDraftReady(true) }
    }).catch(value => { hydrating.current = false; setDraftReady(true); setError(value instanceof Error ? value.message : String(value)) })
  }, [buildId])

  useEffect(() => {
    if (!buildId || !focusedSkillId) { setSupports([]); setWeapons([]); return }
    setSupportSearch('')
    const cached = supportCache[focusedSkillId]
    if (cached) setSupports(cached); else setSupports([])
    Promise.all([
      cached ? Promise.resolve(cached) : json<Support[]>(`${API_URL}/api/catalog/skills/${encodeURIComponent(focusedSkillId)}/supports`),
      json<Weapon[]>(`${API_URL}/api/catalog/builds/${encodeURIComponent(buildId)}/skills/${encodeURIComponent(focusedSkillId)}/weapons`),
    ]).then(([supportRows, weaponRows]) => {
      setSupports(supportRows)
      setSupportCache(current => ({ ...current, [focusedSkillId]: supportRows }))
      const compatible = new Set(supportRows.map(value => value.support_id))
      setRows(current => current.map((row, index) => index === focus && row.skillId === focusedSkillId
        ? { ...row, supportIds: row.supportIds.filter(id => compatible.has(id)).slice(0, SUPPORTS_PER_SKILL) }
        : row))
      setWeapons(weaponRows)
      const restoring = hydrating.current && initialDraft?.buildId === buildId && initialDraft.rows[focus]?.skillId === focusedSkillId
      const restoredWeapon = restoring && initialDraft?.weaponId && weaponRows.some(value => value.base_item_id === initialDraft.weaponId) ? initialDraft.weaponId : ''
      setWeaponId(current => current && weaponRows.some(value => value.base_item_id === current) ? current : restoredWeapon)
      if (hydrating.current) { hydrating.current = false; setDraftReady(true) }
    }).catch(value => { hydrating.current = false; setDraftReady(true); setError(value instanceof Error ? value.message : String(value)) })
  }, [buildId, focusedSkillId])

  useEffect(() => {
    setAffixes([])
    const restoring = hydrating.current && initialDraft?.buildId === buildId && initialDraft?.weaponId === weaponId
    if (!restoring) { setSelectedAffixes([]); setRolls({}) }
    if (!weaponId) return
    const parsedItemLevel = Number(itemLevel)
    if (!Number.isInteger(parsedItemLevel) || parsedItemLevel < 1 || parsedItemLevel > 100) return
    const query = new URLSearchParams({ base_item_id: weaponId, item_level: String(parsedItemLevel) })
    json<Affix[]>(`${API_URL}/api/catalog/items/affixes?${query}`).then(data => {
      setAffixes(data)
      if (restoring && initialDraft) {
        const allowed = new Set(data.map(affix => affix.modifier_id))
        const restoredIds = initialDraft.selectedAffixes.filter(id => allowed.has(id)).slice(0, 6)
        setSelectedAffixes(restoredIds)
        setRolls(Object.fromEntries(restoredIds.map(id => [id, initialDraft.rolls[id] || {}])))
      }
    }).catch(value => setError(value instanceof Error ? value.message : String(value)))
  }, [weaponId, itemLevel])

  useEffect(() => {
    if (!draftReady) return
    writePlannerDraft({ version: 1, buildId, rows, focus, level, weaponId, itemLevel, selectedAffixes, rolls, fixedEquipment: fixedSelections, enemyFire, enemyCold, enemyLightning, enemyChaos, targetHits, dotUptime })
  }, [draftReady, buildId, rows, focus, level, weaponId, itemLevel, selectedAffixes, rolls, fixedSelections, enemyFire, enemyCold, enemyLightning, enemyChaos, targetHits, dotUptime])

  const calculationSignature = useMemo(() => JSON.stringify({ buildId, level, focus, rows, weaponId, itemLevel, selectedAffixes, rolls, fixedSelections, enemyFire, enemyCold, enemyLightning, enemyChaos, targetHits, dotUptime }), [buildId, level, focus, rows, weaponId, itemLevel, selectedAffixes, rolls, fixedSelections, enemyFire, enemyCold, enemyLightning, enemyChaos, targetHits, dotUptime])
  useEffect(() => { setResult(null) }, [calculationSignature])

  function chooseSkill(index: number, id: string) {
    setRows(current => current.map((row, rowIndex) => rowIndex === index ? { skillId: id, supportIds: [] } : row))
    setFocus(index); setError('')
  }
  function clearSkill(index: number) {
    setRows(current => current.map((row, rowIndex) => rowIndex === index ? { skillId: '', supportIds: [] } : row))
    setError('')
  }
  function toggleSupport(id: string) {
    setRows(current => current.map((row, rowIndex) => {
      if (rowIndex !== focus) return row
      if (row.supportIds.includes(id)) return { ...row, supportIds: row.supportIds.filter(value => value !== id) }
      if (row.supportIds.length >= SUPPORTS_PER_SKILL) return row
      return { ...row, supportIds: [...row.supportIds, id] }
    }))
    setError('')
  }
  function setFixedSelection(slotId: string, selection: FixedItemSelection) { setFixedSelections(current => ({ ...current, [slotId]: selection })); setError('') }
  function clearFixedSelection(slotId: string) { setFixedSelections(current => { const next = { ...current }; delete next[slotId]; return next }); setError('') }

  function resetPlanner() {
    const candidates = skills.filter(value => !value.hidden_from_default_picker)
    const preferred = candidates.find(value => value.slot_name === 'pool_2') || candidates.find(value => value.slot_name === 'pool_1') || candidates[0]
    const next = emptyRows(); if (preferred) next[0] = { skillId: preferred.skill_id, supportIds: [] }
    setRows(next); setFocus(0); setLevel('83'); setWeaponId(''); setItemLevel('100'); setSelectedAffixes([]); setRolls({}); setFixedSelections({})
    setEnemyFire('0'); setEnemyCold('0'); setEnemyLightning('0'); setEnemyChaos('0'); setTargetHits(''); setDotUptime(''); setResult(null); setError('')
  }

  async function calculate() {
    if (!buildId || !focusedSkillId) return
    setLoading(true); setResult(null); setError('')
    try {
      const mercenaryLevel = boundedNumeric(level, 'Mercenary level', 1, 100, true)
      const fire = boundedNumeric(enemyFire, 'Enemy fire resistance', -200, 200)
      const cold = boundedNumeric(enemyCold, 'Enemy cold resistance', -200, 200)
      const lightning = boundedNumeric(enemyLightning, 'Enemy lightning resistance', -200, 200)
      const chaos = boundedNumeric(enemyChaos, 'Enemy chaos resistance', -200, 200)
      const parsedItemLevel = weaponId ? boundedNumeric(itemLevel, 'Weapon item level', 1, 100, true) : 100
      const targetHitsValue = targetHits.trim() ? positiveNumeric(targetHits, 'Target hits per repeat') : null
      const dotPercent = dotUptime.trim() ? boundedNumeric(dotUptime, 'Direct DoT uptime', 0, 100) : null
      const modifierRolls = compileModifierRolls(selectedDefs, rolls)
      const fixedEquipment = compileFixedEquipment(fixedSelections)
      const response = await fetch(`${API_URL}/api/calculate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          build_id: buildId, mercenary_level: mercenaryLevel, skill_id: focusedSkillId, support_ids: focusedSupports,
          weapon_base_item_id: weaponId || null, weapon_item_level: parsedItemLevel, weapon_modifier_rolls: modifierRolls, fixed_equipment: fixedEquipment,
          enemy: { level: 83, fire_resistance_percent: fire, cold_resistance_percent: cold, lightning_resistance_percent: lightning, chaos_resistance_percent: chaos },
          scenario: {
            target_hits_per_repeat: targetHitsValue,
            target_event_provenance: targetHitsValue === null ? null : `planner:user-entered target hits per repeat=${targetHitsValue}`,
            direct_dot_uptime_fraction: dotPercent === null ? null : dotPercent / 100,
            direct_dot_uptime_provenance: dotPercent === null ? null : `planner:user-entered direct DoT uptime=${dotPercent}%`,
          },
        }),
      })
      const payload = await response.json().catch(() => null)
      if (!response.ok) {
        const detail = payload && typeof payload === 'object' && 'detail' in payload ? (payload as { detail?: unknown }).detail : null
        throw new Error(typeof detail === 'string' ? detail : `Calculation failed (${response.status})`)
      }
      setResult(payload as CalculationResponse)
    } catch (value) { setError(value instanceof Error ? value.message : String(value)) }
    finally { setLoading(false) }
  }

  const hitDps = numberFrom(result?.result, ['hit_dps', 'intrinsic_formation_hit_dps', 'scheduled_hit_dps'])
  const dotDps = numberFrom(result?.result, ['standalone_dps', 'active_dps', 'direct_dot_dps'])
  const averageHit = numberFrom(result?.result, ['average_mitigated_hit', 'average_damage_per_target_event', 'average_hit'])
  const actionRate = numberFrom(result?.result, ['action_rate_per_second', 'action_rate_per_totem', 'effective_action_rate'])
  const headline = hitDps ?? dotDps
  const healthText = apiHealth === 'ready' ? 'API ready' : apiHealth === 'down' ? 'API unavailable' : 'checking API'

  return <div className="pp-app">
    <header className="pp-header">
      <a className="pp-brand" href="#planner" aria-label="Path of Mercenaries planner"><span className="pp-brand-mark" aria-hidden="true">✦</span><span><small>PATH OF</small><strong>MERCENARIES</strong></span></a>
      <nav aria-label="Planner navigation"><a className="active" href="#planner">Planner</a><a href="#skills">Skills</a><a href="#results">Results</a></nav>
      <div className={`pp-engine ${apiHealth}`} title={`Planner service: ${healthText}`}><i />3.29.3.1.2 · {healthText} · {draftReady ? 'draft saved locally' : 'restoring draft'}</div>
    </header>

    <main id="planner" className="pp-workbench">
      <aside className="pp-panel pp-left">
        <section><h2>Mercenary</h2>
          <label>Build archetype<select value={buildId} onChange={event => setBuildId(event.target.value)}>{builds.map(value => <option key={value.build_id} value={value.build_id}>{value.build_name || value.build_id}{value.infamous ? ' · Infamous' : ''}</option>)}</select></label>
          {build && <div className="pp-merc-card"><span className="portrait">M</span><span><strong>{build.build_name || build.build_id}</strong><small>{build.house_name || build.class_id}</small><small>{build.actor_name || build.actor_id || 'Actor identity unresolved'}</small></span>{build.infamous && <b>Infamous</b>}</div>}
          <label>Mercenary level<input type="number" min="1" max="100" value={level} onChange={event => setLevel(event.target.value)} /></label>
          <button type="button" className="pp-reset" onClick={resetPlanner}>Reset current planner</button>
        </section>
        <section><h2>Enemy & Scenario</h2><div className="pp-res-grid">
          <label>Fire res<input type="number" min="-200" max="200" value={enemyFire} onChange={event => setEnemyFire(event.target.value)} /></label>
          <label>Cold res<input type="number" min="-200" max="200" value={enemyCold} onChange={event => setEnemyCold(event.target.value)} /></label>
          <label>Lightning res<input type="number" min="-200" max="200" value={enemyLightning} onChange={event => setEnemyLightning(event.target.value)} /></label>
          <label>Chaos res<input type="number" min="-200" max="200" value={enemyChaos} onChange={event => setEnemyChaos(event.target.value)} /></label>
        </div>
          <label>Target hits / repeat <em>optional</em><input type="number" min="0.01" step="0.01" placeholder="Leave unresolved" value={targetHits} onChange={event => setTargetHits(event.target.value)} /></label>
          <label>Direct DoT uptime % <em>optional</em><input type="number" min="0" max="100" placeholder="Leave unresolved" value={dotUptime} onChange={event => setDotUptime(event.target.value)} /></label>
          <p className="pp-note">Hit multiplicity and uptime remain unresolved unless explicitly configured. Projectile geometry is never converted into invented DPS.</p>
        </section>
        <section><h2>Mercenary Resistances</h2>
          <div className="pp-defence-grid" aria-label="Mercenary resistance totals are unresolved">
            {['Fire', 'Cold', 'Lightning', 'Chaos'].map(name => <div key={name}><strong>{name}</strong><span>Capped <b>—</b></span><span>Overcap <b>—</b></span></div>)}
          </div>
          <p className="pp-note">Capped and over-cap totals stay unresolved until actor and equipment defensive semantics are validated. No placeholder resistance values are fabricated.</p>
        </section>
      </aside>

      <div className="pp-center">
        <section className="pp-panel pp-equipment"><header><h2>Equipment</h2><span>Calculation-active source closure · exact rolls</span></header>
          <div className="pp-equipment-grid">
            <EquipmentSlot className="mainhand" label="Main Hand" enabled value={weapon?.name} onClick={() => document.getElementById('primary-weapon')?.focus()} />
            {fixedSlots.map(slot => <EquipmentSlot key={slot.slot_id} className={slotClass[slot.slot_id] || ''} label={slotLabel[slot.slot_id] || slot.item_class_id} enabled value={fixedSelections[slot.slot_id]?.itemName} active={activeFixedSlot === slot.slot_id} onClick={() => setActiveFixedSlot(slot.slot_id)} />)}
            <EquipmentSlot className="offhand" label="Off Hand" />
          </div>
          <div className="pp-weapon-select"><label>Primary weapon for focused skill<select id="primary-weapon" value={weaponId} onChange={event => setWeaponId(event.target.value)}><option value="">No weapon selected</option>{weapons.map(value => <option key={value.base_item_id} value={value.base_item_id}>{value.name || value.base_item_id} · {value.item_class_id} · drop {value.drop_level ?? '?'}</option>)}</select></label>
            {weapon && <div><strong>{weapon.name}</strong><span>{weapon.item_class_name || weapon.item_class_id}</span><small>{fmt(weapon.physical_damage_min, 0)}–{fmt(weapon.physical_damage_max, 0)} phys · {weapon.attack_time_ms ? fmt(1000 / weapon.attack_time_ms, 2) : '—'} APS · {weapon.critical_chance_basis_points ? fmt(weapon.critical_chance_basis_points / 100, 2) : '—'}% crit</small></div>}
          </div>
          {weapon && <WeaponAffixEditor affixes={affixes} selectedIds={selectedAffixes} rolls={rolls} itemLevel={itemLevel} onItemLevel={setItemLevel} onSelected={setSelectedAffixes} onRolls={setRolls} onError={setError} />}
          <details className="pp-fixed-drawer" open={Boolean(activeSlot)}><summary>Armour & jewellery <span>{Object.keys(fixedSelections).length}/8 configured</span></summary>
            <FixedEquipmentEditor apiUrl={API_URL} slot={activeSlot} selection={activeFixedSlot ? fixedSelections[activeFixedSlot] : undefined} onChange={selection => activeFixedSlot && setFixedSelection(activeFixedSlot, selection)} onClear={() => activeFixedSlot && clearFixedSelection(activeFixedSlot)} onError={setError} />
          </details>
        </section>

        <section id="skills" className="pp-panel pp-skills"><header><h2>Skill Workspace</h2><span>5 planning rows · 5 supports each · extracted source pool retained</span></header>
          <p className="pp-skill-boundary">These are planning rows, not a claim that the Mercenary simultaneously schedules five skills. The extracted Skill1/Skill2/Skill3 pool identity is shown on each selected skill; multi-skill AI timing remains fail-closed.</p>
          <div className="pp-skill-rack">{rows.map((row, index) => {
            const skill = skills.find(value => value.skill_id === row.skillId)
            const catalog = row.skillId === focusedSkillId ? supports : supportCache[row.skillId] || []
            const linked = row.supportIds.map(id => catalog.find(value => value.support_id === id))
            return <article key={index} className={index === focus ? 'focused' : ''} onClick={() => setFocus(index)}>
              <span className="pp-row-number">{index + 1}</span><GameAssetIcon kind="skills" id={skill?.skill_id} size={56} fallback={skill ? (skill.name || '?').slice(0, 1) : '+'} />
              <div className="pp-skill-copy"><select value={row.skillId} aria-label={`Planning skill row ${index + 1}`} onClick={event => event.stopPropagation()} onChange={event => chooseSkill(index, event.target.value)}><option value="">Select skill</option>{visibleSkills.map(value => <option key={value.skill_id} value={value.skill_id} disabled={value.skill_id !== row.skillId && selectedSkillIds.has(value.skill_id)}>{value.name || value.skill_id} · {friendlyPool(value.slot_name)}</option>)}</select>
                <div><strong>{skill?.name || 'Empty planning row'}</strong>{skill && <span>Lv {skill.selected_skill_level ?? '?'} · {friendlyPool(skill.slot_name)}</span>}</div><p>{skill?.description || 'Choose one of this Mercenary build’s extracted skill candidates.'}</p>
                {skill && <button type="button" className="pp-clear-skill" onClick={event => { event.stopPropagation(); clearSkill(index) }}>Clear row</button>}
              </div>
              <div className="pp-support-chain">{Array.from({ length: SUPPORTS_PER_SKILL }, (_, supportIndex) => { const support = linked[supportIndex]; return <div className={support ? 'filled' : ''} key={supportIndex} title={support ? supportEffectText(support) : 'Empty support position'}>{support ? <GameAssetIcon kind="supports" id={support.support_id} size={32} fallback="◆" /> : <i>+</i>}<span>{support?.name || 'Empty'}</span>{support && <button type="button" aria-label={`Remove ${support.name || support.support_id}`} onClick={event => { event.stopPropagation(); toggleSupport(support.support_id) }}>×</button>}</div> })}</div>
            </article>
          })}</div>
          <div className="pp-support-picker"><div><strong>{focusedSkill ? `Configure ${focusedSkill.name || focusedSkill.skill_id}` : 'Select a skill row'}</strong><span>{focusedSupports.length}/{SUPPORTS_PER_SKILL} supports</span></div>
            {focusedSkill && supports.length > 0 && <label className="pp-support-search">Find compatible support<input type="search" value={supportSearch} placeholder={`Search ${supports.length} compatible supports`} onChange={event => setSupportSearch(event.target.value)} /></label>}
            {focusedSkill && supports.length ? <div className="pp-support-list">{filteredSupports.map(support => { const active = focusedSupports.includes(support.support_id); return <button type="button" key={support.support_id} className={active ? 'selected' : ''} disabled={!active && focusedSupports.length >= SUPPORTS_PER_SKILL} title={supportEffectText(support)} onClick={() => toggleSupport(support.support_id)}><GameAssetIcon kind="supports" id={support.support_id} size={28} fallback={active ? '✓' : '+'} /><span><strong>{support.name || support.support_id}</strong><small>{supportEffectText(support)}</small></span></button> })}</div> : <p className="pp-note">{focusedSkill ? 'No compatible supports returned.' : 'Choose a skill to configure supports.'}</p>}
            {focusedSkill && supports.length > 0 && filteredSupports.length === 0 && <p className="pp-note">No compatible support matches that search.</p>}
            {selectedSupportRows.length > 0 && <details className="pp-support-details"><summary>Selected support source details</summary>{selectedSupportRows.map(support => <div key={support.support_id}><header><GameAssetIcon kind="supports" id={support.support_id} size={28} fallback="◆" /><span><strong>{support.name || support.support_id}</strong><small>{support.evidence_status}</small></span></header>{support.stats.length ? <ul>{support.stats.map(stat => <li key={`${support.support_id}:${stat.ordinal}:${stat.stat_id}`}><code>{stat.stat_id}</code><span>{supportStatValue(stat)}</span><small>{stat.semantic_status}</small></li>)}</ul> : <p>No source stat rows returned.</p>}</div>)}</details>}
          </div>
        </section>
      </div>

      <aside id="results" className="pp-panel pp-results" aria-live="polite"><header><h2>Focused Skill Result</h2><span>No multi-skill summation</span></header>
        <div className="pp-focused"><small>Planning row {focus + 1}</small><strong>{focusedSkill?.name || 'No skill selected'}</strong><span>{focusedSupports.length} support{focusedSupports.length === 1 ? '' : 's'} · {Object.keys(fixedSelections).length} fixed item{Object.keys(fixedSelections).length === 1 ? '' : 's'}</span></div>
        <button className="pp-calc" type="button" disabled={loading || !focusedSkillId || apiHealth === 'down'} onClick={calculate}>{loading ? 'Calculating…' : apiHealth === 'down' ? 'Calculation API unavailable' : 'Calculate Focused Skill'}</button>
        {error && <div className="pp-message warning" role="alert"><strong>Input / request issue</strong><span>{error}</span></div>}
        {!result && !error && <div className="pp-placeholder"><b>◇</b><strong>Ready for calculation</strong><span>Any calculation input change clears the prior result. The validated backend calculates only the focused skill; combined Mercenary DPS remains withheld until scheduler evidence exists.</span></div>}
        {result && <div className="pp-result-stack"><div className={`pp-status ${result.status}`}><span>{result.status === 'ok' ? 'Engine result' : result.status === 'unsupported' ? 'Fail-closed' : 'Request error'}</span><strong>{result.confidence}</strong></div>
          {result.status === 'ok' && <><div className="pp-dps"><span>{hitDps !== undefined ? 'Standalone hit DPS' : dotDps !== undefined ? 'Standalone direct DoT DPS' : 'Validated result'}</span><strong>{headline === undefined ? 'Calculated' : fmt(headline, 1)}</strong></div><div className="pp-metrics"><div><span>Average hit</span><strong>{fmt(averageHit, 1)}</strong></div><div><span>Action rate</span><strong>{fmt(actionRate, 3)}/s</strong></div><div><span>Direct DoT</span><strong>{fmt(dotDps, 1)}</strong></div><div><span>Service time</span><strong>{fmt(result.service_time_ms, 2)} ms</strong></div></div></>}
          {result.unsupported_reasons.length > 0 && <div className="pp-message warning"><strong>Calculation withheld</strong>{result.unsupported_reasons.map(text => <span key={text}>{text}</span>)}</div>}
          {result.warnings.length > 0 && <div className="pp-message warning"><strong>Warnings</strong>{result.warnings.map(text => <span key={text}>{text}</span>)}</div>}
          {result.assumptions.length > 0 && <div className="pp-message"><strong>Explicit assumptions</strong>{result.assumptions.map(text => <span key={text}>{text}</span>)}</div>}
          <details><summary>Calculation payload</summary><pre>{JSON.stringify(result.result, null, 2)}</pre></details></div>}
      </aside>
    </main>
    <footer className="pp-footer"><span>Path of Mercenaries · target patch 3.29.3.1.2</span><span>This product isn’t affiliated with or endorsed by Grinding Gear Games in any way.</span></footer>
  </div>
}
